import React, { createContext, useState, useContext } from 'react';
import { retrieveFromLocalStorage } from '../services/LocalStorageService';
import { journalService } from '../services/JournalService';
import { JournalEntry } from '../types/journal';

// Define the shape of the context
interface RecipeContextType {
  isUploading: boolean;
  error: string | null;
  getIngredients: () => Promise<void>;
  getRecipe: () => void;
  setRecipe: (recipe: unknown) => void;
  journals: JournalEntry[];
  getJournals: () => Promise<void>;
}

// Add FormData interface if not already present
interface Metadata {
  spread_type: string;
  matrix_size: string;
  layout: { position: string; card_name: string; }[];
  readingMode: string;
  focusArea: string;
  experienceLevel: string;
  question: string;
  notes: string;
}

// Create and export the context
export const RecipeContext = createContext<RecipeContextType | undefined>(undefined);

export const useRecipe = () => {
  const context = useContext(RecipeContext);
  if (!context) {
    throw new Error('useRecipe must be used within a RecipeProvider');
  }
  return context;
};

// Context provider
export const RecipeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [recipe, setRecipe] = useState<unknown>({});
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [journals, setJournals] = useState<JournalEntry[]>([]);

  const getIngredients = async () => {
    setIsUploading(true);
    setError(null);
    try {
      // TODO: Implement actual ingredients logic
      console.log('Getting ingredients...');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsUploading(false);
    }
  };

  const getRecipe = async () => {
    setIsUploading(true);
    setError(null);
    const ingredients = retrieveFromLocalStorage('foodList');

    try {
      // TODO: Implement actual recipe logic
      console.log('Getting recipe for ingredients:', ingredients);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsUploading(false);
    }
  };

  const getJournals = async () => {
    setIsUploading(true);
    setError(null);
    try {
      const result = await journalService.getAllEntries();
      setJournals(result);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <RecipeContext.Provider
      value={{
        isUploading,
        error,
        getIngredients,
        getRecipe,
        setRecipe,
        journals,
        getJournals,
      }}
    >
      {children}
    </RecipeContext.Provider>
  );
};
